<template>
  <div class="messages-page">
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <Button icon="pi pi-arrow-left" class="p-button-text" @click="goBack" />
          <h1>Mensagens do WhatsApp</h1>
        </div>
        <div class="instance-info" v-if="currentInstance">
          <Tag :value="getStatusLabel(currentInstance.status)" :severity="getStatusSeverity(currentInstance.status)" />
          <span class="instance-name">{{ currentInstance.instance_name }}</span>
        </div>
      </div>
    </div>
    
    <div class="messages-container" v-if="currentInstance">
      <Splitter class="splitter-container">
        <SplitterPanel :size="30" :minSize="20" class="chat-list-panel">
          <ChatList 
            :instanceId="instanceId" 
            :chats="chats" 
            :loading="loadingChats"
            :selectedChatId="selectedChat ? selectedChat.id : null"
            @chat-selected="selectChat"
            @new-chat="createNewChat"
          />
        </SplitterPanel>
        
        <SplitterPanel :size="70" :minSize="50" class="chat-messages-panel">
          <div v-if="!selectedChat" class="no-chat-selected">
            <i class="pi pi-comments" style="font-size: 4rem; color: var(--text-color-secondary)"></i>
            <h2>Selecione uma conversa</h2>
            <p>Escolha uma conversa da lista ou inicie uma nova</p>
          </div>
          
          <ChatMessages 
            v-else
            :instanceId="instanceId"
            :contact="selectedChat"
            :messages="messages"
            :loading="loadingMessages"
            @send-message="sendMessage"
            @send-media="sendMediaMessage"
            @refresh="fetchChatMessages"
          />
        </SplitterPanel>
      </Splitter>
    </div>
    
    <div v-else-if="loading" class="loading-container">
      <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
      <p>Carregando instância...</p>
    </div>
    
    <div v-else class="error-container">
      <i class="pi pi-exclamation-triangle" style="font-size: 3rem; color: var(--lime)"></i>
      <h2>Instância não encontrada</h2>
      <p>A instância solicitada não existe ou não está disponível</p>
      <Button label="Voltar para Instâncias" icon="pi pi-arrow-left" class="p-button-primary" @click="goBack" />
    </div>
    
    <Toast />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import ChatList from '../components/ChatList.vue';
import ChatMessages from '../components/ChatMessages.vue';

export default {
  name: 'MessagesPage',
  components: {
    ChatList,
    ChatMessages
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    
    const instanceId = computed(() => route.params.instanceId);
    const loading = computed(() => store.getters['whatsapp/isLoading']);
    const currentInstance = computed(() => store.getters['whatsapp/currentInstance']);
    const chats = computed(() => store.getters['whatsapp/allChats']);
    const messages = computed(() => store.getters['whatsapp/chatMessages']);
    
    const loadingChats = ref(false);
    const loadingMessages = ref(false);
    const selectedChat = ref(null);
    
    const statusLabels = {
      'created': 'Criado',
      'connecting': 'Conectando',
      'open': 'Conectado',
      'disconnected': 'Desconectado',
      'close': 'Fechado'
    };
    
    const statusSeverity = {
      'created': 'info',
      'connecting': 'warning',
      'open': 'success',
      'disconnected': 'danger',
      'close': 'danger'
    };
    
    const getStatusLabel = (status) => {
      return statusLabels[status] || status;
    };
    
    const getStatusSeverity = (status) => {
      return statusSeverity[status] || 'info';
    };
    
    // Navega de volta para a lista de instâncias
    const goBack = () => {
      router.push('/whatsapp/instances');
    };
    
    // Carrega os detalhes da instância
    const loadInstance = async () => {
      try {
        await store.dispatch('whatsapp/fetchInstance', { id: instanceId.value });
        fetchChats();
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar os detalhes da instância',
          life: 3000
        });
      }
    };
    
    // Busca as conversas da instância
    const fetchChats = async () => {
      loadingChats.value = true;
      
      try {
        await store.dispatch('whatsapp/fetchChats', { instanceId: instanceId.value });
        
        // Se não houver chat selecionado e existirem chats, seleciona o primeiro
        if (!selectedChat.value && chats.value.length > 0) {
          selectChat(chats.value[0]);
        }
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar as conversas',
          life: 3000
        });
      } finally {
        loadingChats.value = false;
      }
    };
    
    // Seleciona um chat e carrega suas mensagens
    const selectChat = (chat) => {
      selectedChat.value = chat;
      fetchChatMessages();
    };
    
    // Busca as mensagens do chat selecionado
    const fetchChatMessages = async () => {
      if (!selectedChat.value) return;
      
      loadingMessages.value = true;
      
      try {
        await store.dispatch('whatsapp/fetchChatMessages', {
          instanceId: instanceId.value,
          chatId: selectedChat.value.id
        });
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar as mensagens',
          life: 3000
        });
      } finally {
        loadingMessages.value = false;
      }
    };
    
    // Cria uma nova conversa
    const createNewChat = async (newChat) => {
      try {
        // Verifica se a conversa já existe na lista
        const existingChat = chats.value.find(chat => 
          chat.id === newChat.phoneNumber || 
          chat.id === `${newChat.phoneNumber}@c.us`
        );
        
        if (existingChat) {
          selectChat(existingChat);
          
          // Se uma mensagem inicial foi fornecida, envia
          if (newChat.initialMessage) {
            await sendMessage({
              instanceId: instanceId.value,
              destination: existingChat.id,
              message: newChat.initialMessage,
              options: {}
            });
          }
          
          return;
        }
        
        // Formata o número de telefone para o formato usado pelo WhatsApp
        const formattedNumber = `${newChat.phoneNumber}@c.us`;
        
        // Cria um objeto de chat temporário
        const tempChat = {
          id: formattedNumber,
          name: newChat.contactName || formattedNumber,
          lastMessage: null,
          unreadCount: 0
        };
        
        // Seleciona o chat temporário
        selectChat(tempChat);
        
        // Se uma mensagem inicial foi fornecida, envia
        if (newChat.initialMessage) {
          await sendMessage({
            instanceId: instanceId.value,
            destination: formattedNumber,
            message: newChat.initialMessage,
            options: {}
          });
          
          // Recarrega a lista de chats para incluir o novo
          await fetchChats();
        }
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível criar a nova conversa',
          life: 3000
        });
      }
    };
    
    // Envia uma mensagem de texto
    const sendMessage = async (messageData) => {
      try {
        await store.dispatch('whatsapp/sendMessage', messageData);
        
        // Adiciona a mensagem localmente ao estado
        store.commit('whatsapp/addMessageToChat', {
          message: {
            id: Date.now().toString(),
            fromMe: true,
            content: messageData.message,
            timestamp: new Date().toISOString(),
            status: 'sent',
            type: 'text'
          }
        });
        
        // Atualiza a lista de chats após um breve delay para mostrar a última mensagem
        setTimeout(() => {
          fetchChats();
        }, 500);
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível enviar a mensagem',
          life: 3000
        });
      }
    };
    
    // Envia uma mensagem de mídia
    const sendMediaMessage = async (mediaData) => {
      try {
        await store.dispatch('whatsapp/sendMediaMessage', mediaData);
        
        // Adiciona a mensagem localmente ao estado
        store.commit('whatsapp/addMessageToChat', {
          message: {
            id: Date.now().toString(),
            fromMe: true,
            content: URL.createObjectURL(mediaData.file),
            caption: mediaData.caption,
            timestamp: new Date().toISOString(),
            status: 'sent',
            type: mediaData.file.type.startsWith('image/') ? 'image' : 
                  mediaData.file.type.startsWith('video/') ? 'video' : 
                  mediaData.file.type.startsWith('audio/') ? 'audio' : 'document',
            filename: mediaData.file.name
          }
        });
        
        // Atualiza a lista de chats após um breve delay para mostrar a última mensagem
        setTimeout(() => {
          fetchChats();
        }, 500);
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível enviar a mídia',
          life: 3000
        });
      }
    };
    
    // Carrega a instância e chats quando o componente é montado
    onMounted(() => {
      loadInstance();
    });
    
    // Quando o ID da instância muda, recarrega os dados
    watch(() => instanceId.value, () => {
      loadInstance();
    });
    
    return {
      instanceId,
      loading,
      currentInstance,
      chats,
      messages,
      loadingChats,
      loadingMessages,
      selectedChat,
      getStatusLabel,
      getStatusSeverity,
      goBack,
      fetchChats,
      selectChat,
      fetchChatMessages,
      createNewChat,
      sendMessage,
      sendMediaMessage
    };
  }
};
</script>

<style scoped>
.messages-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--ink);
}

.page-header {
  background-color: var(--ink-soft);
  padding: 1rem 1.5rem;
  border-bottom: 2px solid var(--text);
  z-index: 1;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-left h1 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.instance-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.instance-name {
  font-weight: 500;
}

.messages-container {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.splitter-container {
  width: 100%;
  height: 100%;
}

.chat-list-panel {
  background-color: var(--ink-soft);
  border-right: 1px solid var(--line);
}

.chat-messages-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: var(--ink);
}

.no-chat-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: var(--muted);
  padding: 2rem;
}

.no-chat-selected h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem;
  color: var(--text);
}

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80vh;
  text-align: center;
  gap: 1rem;
}

.error-container h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0.5rem 0;
  color: var(--text);
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .chat-list-panel {
    min-width: 100%;
  }
  
  .splitter-container {
    flex-direction: column;
  }
}
</style> 